/**
 * Serialises third-party tag start-up so no single frame owns all of it.
 *
 * PERF: measured on the home page (4x CPU, Slow 4G), third-party tags accounted for
 * 83% of long-task time and 90% of scroll script time — 67 long tasks totalling
 * 7,829ms, clustered between 15.7s and 21s. The cause was not a missing `defer`:
 * every `<Script>` is already `lazyOnload` and gated behind the idle gate. The cause
 * was that `lazyOnload` waits for `window.load` and the idle callback then released
 * GTM, GA, the sGTM container, three social pixels and Smartlook *in the same frame*,
 * colliding with our own hydration.
 *
 * This queue hands out one idle slice per task instead, so the browser can paint and
 * respond between tags. Order is preserved, so callers control priority: enqueue
 * attribution-critical tags first.
 *
 * NOTE: nothing here decides *whether* a tag fires — that stays with the existing
 * gates. This only spreads out *when* each one starts.
 */
type Task = () => void;

const queue: Task[] = [];
let draining = false;

const idle = (cb: (deadline?: IdleDeadline) => void, timeout = 1000) =>
  typeof window !== "undefined" && (window as any).requestIdleCallback
    ? (window as any).requestIdleCallback(cb, { timeout })
    : setTimeout(cb, 200);

/**
 * Drain as much as fits in the current idle slice, then yield.
 *
 * One-task-per-callback would be safer for frame budget but too slow for
 * attribution: `requestIdleCallback`'s timeout is the WORST case, so a queue of
 * seven tags could take seven timeouts to finish. Using `timeRemaining()` means a
 * genuinely idle browser drains the queue almost immediately, while a busy one
 * still hands back the thread after each task.
 */
const drain = (deadline?: IdleDeadline) => {
  do {
    const task = queue.shift();
    if (!task) {
      draining = false;
      return;
    }
    try {
      task();
    } catch {
      // A failing tag must never stall the ones behind it.
    }
    // 4ms leaves room for the task we might start next without overrunning the frame.
  } while (deadline && deadline.timeRemaining() > 4);
  idle(drain);
};

/** Queue a tag initialiser to run in an idle slice. Order is preserved. */
export const enqueueTag = (task: Task) => {
  queue.push(task);
  if (draining) return;
  draining = true;
  idle(drain);
};
